// Boutique geoVINUM — listing avancé Etsy/Gelato avec :
// — Search bar rectangle (5px radius)
// — Sidebar gauche : filtres Type + Région (sticky)
// — Tri (Recommandé, Best sellers, Nouveautés, Prix ↑/↓)
// — Section "Best sellers" au-dessus de la grille (cachée si filtres actifs)
// Contenu traduit FR / EN / ES / PT via dictionnaire local `COPY`.
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Search, ChevronDown, Flame, X, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useCurrency } from "@/context/CurrencyContext";
import { apiFetch, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";

// ---------------------------------------------------------------------------
// i18n dictionary — every visible string in FR / EN / ES / PT.
// Kept local to the page (rather than global i18n) because the shop copy
// is scoped and helps translators see the whole surface at once.
// ---------------------------------------------------------------------------
const COPY = {
  fr: {
    meta_title: "Boutique geoVINUM — Cartes des vignobles",
    meta_desc:  "Cartes des appellations viticoles imprimées en France.",
    intent: {
      learn:  { title: "Apprendre le vin",     intro: "Nos cartes pédagogiques — comprendre un vignoble sans se perdre." },
      gift:   { title: "Idées cadeaux",         intro: "Nos meilleures cartes à offrir à un amateur, un pro ou un curieux." },
      region: { title: "Découvrir une région",  intro: "Explorez appellations, terroirs et vignobles à travers le monde." },
      decor:  { title: "Cartes pour décorer",   intro: "Les cartes qui habillent un salon, un bureau ou une cave." },
    },
    sort_options: [
      { value: "featured",     label: "Recommandé" },
      { value: "best_sellers", label: "Best sellers" },
      { value: "newest",       label: "Nouveautés" },
      { value: "price_asc",    label: "Prix croissant" },
      { value: "price_desc",   label: "Prix décroissant" },
    ],
    hero_title:   "Boutique des cartes",
    hero_intro:   "Cartes des appellations viticoles, impression giclée premium, livraison mondiale.",
    intent_clear: "Voir toutes les cartes",
    search_placeholder: "Rechercher une carte, une région, un type…",
    sort_label:   "Trier :",
    best_sellers: "Best sellers",
    filter_type:  "Type",
    filter_region:"Région",
    filter_all_m: "Tous",
    filter_all_f: "Toutes",
    reset:        "Réinitialiser",
    catalog_all:  "Tout le catalogue",
    results_singular: (n) => `${n} résultat`,
    results_plural:   (n) => `${n} résultats`,
    error_prefix: "Erreur :",
    empty_title:  "Aucune carte ne correspond à votre recherche.",
    empty_cta:    "Voir tout le catalogue",
    from_price:   "à partir de",
  },
  en: {
    meta_title: "geoVINUM Shop — Vineyard maps",
    meta_desc:  "Wine appellation maps printed in France.",
    intent: {
      learn:  { title: "Learn wine",             intro: "Our educational maps — understand a vineyard without getting lost." },
      gift:   { title: "Gift ideas",             intro: "Our best maps to gift a wine lover, a pro or the merely curious." },
      region: { title: "Discover a region",      intro: "Explore appellations, terroirs and vineyards around the world." },
      decor:  { title: "Maps for decor",         intro: "Maps that dress up a living room, an office or a cellar." },
    },
    sort_options: [
      { value: "featured",     label: "Recommended" },
      { value: "best_sellers", label: "Best sellers" },
      { value: "newest",       label: "New arrivals" },
      { value: "price_asc",    label: "Price · low to high" },
      { value: "price_desc",   label: "Price · high to low" },
    ],
    hero_title:   "Map shop",
    hero_intro:   "Wine-appellation maps, premium giclée print, worldwide shipping.",
    intent_clear: "See all maps",
    search_placeholder: "Search for a map, a region, a type…",
    sort_label:   "Sort:",
    best_sellers: "Best sellers",
    filter_type:  "Type",
    filter_region:"Region",
    filter_all_m: "All",
    filter_all_f: "All",
    reset:        "Reset",
    catalog_all:  "Full catalogue",
    results_singular: (n) => `${n} result`,
    results_plural:   (n) => `${n} results`,
    error_prefix: "Error:",
    empty_title:  "No map matches your search.",
    empty_cta:    "See the full catalogue",
    from_price:   "from",
  },
  es: {
    meta_title: "Tienda geoVINUM — Mapas de viñedos",
    meta_desc:  "Mapas de denominaciones vitivinícolas impresos en Francia.",
    intent: {
      learn:  { title: "Aprender del vino",       intro: "Nuestros mapas pedagógicos — entender un viñedo sin perderse." },
      gift:   { title: "Ideas de regalo",         intro: "Nuestros mejores mapas para regalar a un aficionado, un pro o un curioso." },
      region: { title: "Descubrir una región",    intro: "Explora denominaciones, terroirs y viñedos por todo el mundo." },
      decor:  { title: "Mapas para decorar",      intro: "Mapas que visten un salón, una oficina o una bodega." },
    },
    sort_options: [
      { value: "featured",     label: "Recomendado" },
      { value: "best_sellers", label: "Más vendidos" },
      { value: "newest",       label: "Novedades" },
      { value: "price_asc",    label: "Precio · de menor a mayor" },
      { value: "price_desc",   label: "Precio · de mayor a menor" },
    ],
    hero_title:   "Tienda de mapas",
    hero_intro:   "Mapas de denominaciones, impresión giclée premium, envío mundial.",
    intent_clear: "Ver todos los mapas",
    search_placeholder: "Buscar un mapa, una región, un tipo…",
    sort_label:   "Ordenar:",
    best_sellers: "Más vendidos",
    filter_type:  "Tipo",
    filter_region:"Región",
    filter_all_m: "Todos",
    filter_all_f: "Todas",
    reset:        "Restablecer",
    catalog_all:  "Todo el catálogo",
    results_singular: (n) => `${n} resultado`,
    results_plural:   (n) => `${n} resultados`,
    error_prefix: "Error:",
    empty_title:  "Ningún mapa coincide con tu búsqueda.",
    empty_cta:    "Ver todo el catálogo",
    from_price:   "desde",
  },
  pt: {
    meta_title: "Loja geoVINUM — Mapas de vinhedos",
    meta_desc:  "Mapas de denominações vitivinícolas impressos em França.",
    intent: {
      learn:  { title: "Aprender o vinho",        intro: "Os nossos mapas pedagógicos — entender um vinhedo sem se perder." },
      gift:   { title: "Ideias de presente",      intro: "Os nossos melhores mapas para oferecer a um apreciador, um profissional ou um curioso." },
      region: { title: "Descobrir uma região",    intro: "Explore denominações, terroirs e vinhedos por todo o mundo." },
      decor:  { title: "Mapas para decorar",      intro: "Mapas que vestem uma sala, um escritório ou uma cave." },
    },
    sort_options: [
      { value: "featured",     label: "Recomendado" },
      { value: "best_sellers", label: "Mais vendidos" },
      { value: "newest",       label: "Novidades" },
      { value: "price_asc",    label: "Preço · crescente" },
      { value: "price_desc",   label: "Preço · decrescente" },
    ],
    hero_title:   "Loja de mapas",
    hero_intro:   "Mapas de denominações, impressão giclée premium, envio mundial.",
    intent_clear: "Ver todos os mapas",
    search_placeholder: "Procurar um mapa, uma região, um tipo…",
    sort_label:   "Ordenar:",
    best_sellers: "Mais vendidos",
    filter_type:  "Tipo",
    filter_region:"Região",
    filter_all_m: "Todos",
    filter_all_f: "Todas",
    reset:        "Repor",
    catalog_all:  "Todo o catálogo",
    results_singular: (n) => `${n} resultado`,
    results_plural:   (n) => `${n} resultados`,
    error_prefix: "Erro:",
    empty_title:  "Nenhum mapa corresponde à sua pesquisa.",
    empty_cta:    "Ver todo o catálogo",
    from_price:   "a partir de",
  },
};

const ShopPage = () => {
  const { formatFromEUR: fmtEUR } = useCurrency();
  const lang = useLang() || "fr";
  const c = COPY[lang] || COPY.fr;

  const [data, setData] = useState({ items: [], facets: { product_types: [], regions: [] }, total: 0 });
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [region, setRegion] = useState("");
  const [sort, setSort] = useState("featured");
  const [sortOpen, setSortOpen] = useState(false);
  // ?intent=learn|gift|region|decor — set by the Home "Vous cherchez quoi ?"
  // tiles. Only used to tune the hero copy for now; when we introduce
  // intent-driven filtering (WSET level for `learn`, price cap for `gift`,
  // etc.) it will also feed the query params below.
  const [searchParams, setSearchParams] = useSearchParams();
  const intent = searchParams.get("intent") || "";
  const intentCopy = c.intent[intent];

  // Pre-fill the search box from `?q=…`.
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    if (urlQ && urlQ !== q) setQ(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      const { ok, data } = await apiFetch(`/api/shop/products?sort=best_sellers&limit=6`);
      if (ok) setBestSellers(data.items || []);
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchList = async () => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (q.trim()) params.append("q", q.trim());
      if (type) params.append("product_type", type);
      if (region) params.append("region", region);
      if (sort) params.append("sort", sort);
      const path = `/api/shop/products${params.toString() ? `?${params.toString()}` : ""}`;
      const { ok, data, error: err } = await apiFetch(path);
      if (cancelled) return;
      if (!ok) {
        setError(err || "Request failed");
      } else {
        setData(data);
      }
      setLoading(false);
    };
    fetchList();
    return () => { cancelled = true; };
  }, [q, type, region, sort]);

  const sortLabel = useMemo(() => c.sort_options.find((o) => o.value === sort)?.label, [sort, c]);
  const hasActiveFilters = type || region || q.trim();

  return (
    <Layout testId="page-shop">
      <SEO title={c.meta_title} description={c.meta_desc} />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-10 bg-[color:var(--gv-mist)] border-b border-[color:var(--gv-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          {intentCopy && (
            <div className="inline-block text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2" data-testid="shop-intent-chip">
              {intentCopy.title}
            </div>
          )}
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-3 leading-[1.05]">
            {intentCopy?.title || c.hero_title}
          </h1>
          <p className="text-[14px] md:text-[15px] text-[color:var(--gv-ink)]/65 max-w-2xl mx-auto">
            {intentCopy?.intro || c.hero_intro}
          </p>
          {intent && (
            <button
              onClick={() => setSearchParams({})}
              className="mt-3 inline-flex items-center gap-1 text-[12px] text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-pink)]"
              data-testid="shop-intent-clear"
            >
              <X size={12} /> {c.intent_clear}
            </button>
          )}
        </div>
      </section>

      {/* Search + sort sticky bar */}
      <section className="bg-white border-b border-[color:var(--gv-line)] sticky top-[64px] z-20 backdrop-blur supports-[backdrop-filter]:bg-white/85">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          <div className="relative flex-1 max-w-xl">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/45" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={c.search_placeholder}
              className="w-full h-10 pl-10 pr-9 rounded-[5px] border border-[color:var(--gv-line)] bg-white text-[14px] text-[color:var(--gv-ink)] placeholder:text-[color:var(--gv-ink)]/40 focus:border-[color:var(--gv-pink)]/40 focus:ring-1 focus:ring-[color:var(--gv-pink)]/20 outline-none transition-all"
              data-testid="shop-search-input"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40 hover:text-[color:var(--gv-ink)]/70" data-testid="shop-search-clear">
                <X size={14} />
              </button>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setSortOpen((v) => !v)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-[5px] border border-[color:var(--gv-line)] bg-white text-[13px] text-[color:var(--gv-ink)] hover:border-[color:var(--gv-ink)]/30 transition-colors"
              data-testid="shop-sort-toggle"
            >
              {c.sort_label} <span className="font-medium">{sortLabel}</span>
              <ChevronDown size={14} className={`transition-transform ${sortOpen ? "rotate-180" : ""}`} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-lg min-w-[200px] py-1" data-testid="shop-sort-menu">
                {c.sort_options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSort(opt.value); setSortOpen(false); }}
                    className={`block w-full text-left px-3 py-1.5 text-[13px] transition-colors ${sort === opt.value ? "bg-[color:var(--gv-pink)]/8 text-[color:var(--gv-pink)]" : "hover:bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]"}`}
                    data-testid={`shop-sort-${opt.value}`}
                  >{opt.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Best sellers strip (only when no filters) */}
      {!hasActiveFilters && bestSellers.length > 0 && (
        <section className="py-8 md:py-10 bg-white border-b border-[color:var(--gv-line)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-2 mb-5">
              <Flame size={18} className="text-[color:var(--gv-pink)]" />
              <h2 className="font-display text-2xl text-[color:var(--gv-ink)]">{c.best_sellers}</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {bestSellers.map((it, idx) => <ProductCard key={it.product_id} it={it} compact rank={idx + 1} fromLabel={c.from_price} />)}
            </div>
          </div>
        </section>
      )}

      {/* Main grid with left sidebar */}
      <section className="py-10 md:py-14 bg-[color:var(--gv-mist)]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8 items-start">
          {/* Sidebar filters */}
          <aside className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 lg:sticky lg:top-[160px] self-start" data-testid="shop-sidebar">
            <FilterGroup
              title={c.filter_type}
              options={data.facets.product_types}
              value={type}
              onChange={setType}
              prefix="type"
              allLabel={c.filter_all_m}
            />
            <div className="border-t border-[color:var(--gv-line)] my-5"></div>
            <FilterGroup
              title={c.filter_region}
              options={data.facets.regions.slice(0, 20)}
              value={region}
              onChange={setRegion}
              prefix="region"
              scrollable
              allLabel={c.filter_all_f}
            />
            {hasActiveFilters && (
              <button
                onClick={() => { setQ(""); setType(""); setRegion(""); }}
                className="mt-5 text-[12px] text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-pink)] inline-flex items-center gap-1"
                data-testid="clear-filters"
              >
                <X size={12} /> {c.reset}
              </button>
            )}
          </aside>

          {/* Results grid */}
          <div>
            <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
              <h2 className="font-display text-xl md:text-2xl text-[color:var(--gv-ink)]">
                {hasActiveFilters
                  ? (data.total > 1 ? c.results_plural(data.total) : c.results_singular(data.total))
                  : c.catalog_all}
              </h2>
            </div>

            {loading ? (
              <div className="py-16 text-center"><Loader2 size={24} className="animate-spin mx-auto text-[color:var(--gv-pink)]" /></div>
            ) : error ? (
              <div className="py-12 text-center text-red-600 text-[13px]" data-testid="shop-error">{c.error_prefix} {error}</div>
            ) : data.items.length === 0 ? (
              <div className="py-16 text-center text-[color:var(--gv-ink)]/50 bg-white border border-[color:var(--gv-line)] rounded-[5px]">
                <MapPin size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-[14px] mb-3">{c.empty_title}</p>
                <button onClick={() => { setQ(""); setType(""); setRegion(""); }} className="text-[13px] text-[color:var(--gv-pink)] hover:underline">{c.empty_cta}</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {data.items.map((it) => <ProductCard key={it.product_id} it={it} fromLabel={c.from_price} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

const FilterGroup = ({ title, options, value, onChange, prefix, scrollable = false, allLabel = "All" }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/45 mb-2.5 font-medium">{title}</div>
    <div className={`space-y-1 ${scrollable ? "max-h-[320px] overflow-y-auto pr-1 -mr-1" : ""}`}>
      <FilterRow active={!value} onClick={() => onChange("")} label={allLabel} testId={`filter-${prefix}-all`} />
      {options.map((opt) => (
        <FilterRow
          key={opt.slug}
          active={value === opt.slug}
          onClick={() => onChange(value === opt.slug ? "" : opt.slug)}
          label={opt.label}
          count={opt.count}
          testId={`filter-${prefix}-${opt.slug}`}
        />
      ))}
    </div>
  </div>
);

const FilterRow = ({ active, onClick, label, count, testId }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-[5px] text-[13px] transition-colors ${
      active
        ? "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] font-medium"
        : "text-[color:var(--gv-ink)]/80 hover:bg-[color:var(--gv-mist)]"
    }`}
    data-testid={testId}
  >
    <span className="truncate">{label}</span>
    {count != null && (
      <span className={`text-[11px] tabular-nums shrink-0 ml-2 ${active ? "text-[color:var(--gv-pink)]/70" : "text-[color:var(--gv-ink)]/40"}`}>{count}</span>
    )}
  </button>
);

const ProductCard = ({ it, compact = false, rank, fromLabel = "from" }) => {
  const { formatFromEUR } = useCurrency();
  return (
  <Link
    to={`/shop/${it.product_id}`}
    className="group block bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)]/40 hover:shadow-md transition-all relative"
    data-testid={`shop-card-${it.product_id}`}
  >
    {rank && (
      <div className="absolute top-2 left-2 z-10 bg-white/95 backdrop-blur text-[color:var(--gv-pink)] text-[11px] font-display px-2 py-0.5 rounded-[5px] border border-[color:var(--gv-pink)]/20">
        #{rank}
      </div>
    )}
    {/* Editorial badge — one per product max. Rendered top-right so it
        doesn't clash with the best-seller rank tag when both apply. */}
    {it.meta?.badge_meta && (
      <div
        className="absolute top-2 right-2 z-10 bg-[color:var(--gv-ink)] text-white text-[10.5px] font-medium px-2 py-1 rounded-[5px] flex items-center gap-1 shadow-sm"
        data-testid={`shop-badge-${it.product_id}`}
      >
        <span aria-hidden>{it.meta.badge_meta.emoji}</span>
        <span className="uppercase tracking-wider">{it.meta.badge_meta.label}</span>
      </div>
    )}
    <div className="aspect-[3/4] bg-[color:var(--gv-mist)] overflow-hidden">
      <img
        src={`${API}${it.preview}${it.preview && it.preview.includes("?") ? "&" : "?"}w=${compact ? 400 : 600}`}
        alt={it.title}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        onError={(e) => { e.currentTarget.src = "/images/educativos.jpg"; }}
      />
    </div>
    <div className={compact ? "p-3" : "p-4"}>
      <div className={`font-display ${compact ? "text-[13px]" : "text-[15px]"} text-[color:var(--gv-ink)] mb-1.5 group-hover:text-[color:var(--gv-pink)] transition-colors leading-snug line-clamp-2`}>
        {it.title}
      </div>
      {!compact && it.region_label && (
        <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/45 mb-2">{it.region_label}</div>
      )}
      <div className="flex items-center justify-between text-[12px]">
        <span className="text-[color:var(--gv-ink)]/55">{fromLabel}</span>
        <span className="font-display text-[14px] text-[color:var(--gv-ink)]">{formatFromEUR(it.from_amount_cents)}</span>
      </div>
    </div>
  </Link>
  );
};

export default ShopPage;
